// Admin Feedback Component
// Simplified feedback management component that uses existing feedback API

import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { apiClient } from '@/api/base44Client';
import { toast } from '@/components/ui/use-toast';
import { 
  Search, 
  Eye, 
  CheckCircle, 
  Clock, 
  AlertCircle, 
  Bug, 
  Lightbulb, 
  MessageCircle,
  Mail,
  Calendar,
  User,
  ExternalLink,
  RefreshCw,
  Shield,
  TrendingUp
} from 'lucide-react';
import Icon from '@/components/ui/Icon';

const AdminFeedback = () => {
  const [feedback, setFeedback] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [stats, setStats] = useState(null);
  
  // Filters
  const [filters, setFilters] = useState({
    status: 'all',
    feedback_type: 'all',
    priority: 'all',
    search: ''
  });

  useEffect(() => {
    loadFeedback();
    loadStats();
  }, []);

  const loadFeedback = async (customFilters = null) => {
    try {
      setLoading(true);
      const filtersToUse = customFilters || filters;
      const data = await apiClient.Feedback.getAll(50, 0, filtersToUse);
      setFeedback(data);
    } catch (error) {
      console.error('Error loading feedback:', error);
      toast({
        title: "Error loading feedback",
        description: "Failed to load feedback data.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const data = await apiClient.Feedback.getStats();
      setStats(data);
    } catch (error) {
      console.error('Error loading feedback stats:', error);
    }
  };

  const handleFilterChange = (key, value) => {
    const newFilters = {
      ...filters,
      [key]: value
    };
    setFilters(newFilters);
    setTimeout(() => {
      loadFeedback(newFilters);
    }, 100);
  };

  const handleViewDetails = (feedbackItem) => {
    setSelectedFeedback(feedbackItem);
    setIsDetailModalOpen(true);
  };

  const handleUpdateStatus = async (id, newStatus, adminNotes = null) => {
    try {
      setIsUpdating(true);
      await apiClient.Feedback.update(id, {
        status: newStatus,
        admin_notes: adminNotes
      });
      
      toast({
        title: "Status updated",
        description: `Feedback status changed to ${newStatus}.`,
      });
      
      loadFeedback();
    } catch (error) {
      console.error('Error updating feedback:', error);
      toast({
        title: "Error updating feedback",
        description: "Failed to update feedback status.",
        variant: "destructive"
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'new': return <Clock className="w-4 h-4" />;
      case 'in_progress': return <AlertCircle className="w-4 h-4" />;
      case 'resolved': return <CheckCircle className="w-4 h-4" />;
      case 'closed': return <CheckCircle className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'new': return 'bg-blue-100 text-blue-800';
      case 'in_progress': return 'bg-yellow-100 text-yellow-800';
      case 'resolved': return 'bg-green-100 text-green-800';
      case 'closed': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'bug': return <Bug className="w-4 h-4" />;
      case 'feature_request': return <Lightbulb className="w-4 h-4" />;
      case 'general': return <MessageCircle className="w-4 h-4" />;
      default: return <MessageCircle className="w-4 h-4" />;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return 'bg-red-100 text-red-800';
      case 'high': return 'bg-orange-100 text-orange-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading && feedback.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <Icon name="loader-circle" size="xl" className="animate-spin text-brand-600" />
        <span className="ml-2 text-gray-600">Loading feedback...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          {/* Bug Reports */}
          <div className="p-4 border rounded-lg bg-red-50 border-red-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-red-600 mb-1">Bug Reports</p>
                <p className="text-2xl font-bold text-red-800">{stats.byType?.bug || 0}</p>
              </div>
              <Bug className="w-8 h-8 text-red-600" />
            </div>
          </div>

          {/* Feature Requests */}
          <div className="p-4 border rounded-lg bg-purple-50 border-purple-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-brand-600 mb-1">Feature Requests</p>
                <p className="text-2xl font-bold text-brand-800">{stats.byType?.feature_request || 0}</p>
              </div>
              <Lightbulb className="w-8 h-8 text-brand-600" />
            </div>
          </div>

          {/* General Feedback */}
          <div className="p-4 border rounded-lg bg-blue-50 border-blue-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-blue-600 mb-1">General Feedback</p>
                <p className="text-2xl font-bold text-blue-800">{stats.byType?.general || 0}</p>
              </div>
              <MessageCircle className="w-8 h-8 text-blue-600" />
            </div>
          </div>

          {/* New Feedback */}
          <div className="p-4 border rounded-lg bg-yellow-50 border-yellow-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-yellow-600 mb-1">New</p>
                <p className="text-2xl font-bold text-yellow-800">{stats.byStatus?.new || 0}</p>
              </div>
              <Clock className="w-8 h-8 text-yellow-600" />
            </div>
          </div>

          {/* Urgent Priority */}
          <div className="p-4 border rounded-lg bg-orange-50 border-orange-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-orange-600 mb-1">Urgent</p>
                <p className="text-2xl font-bold text-orange-800">{stats.byPriority?.urgent || 0}</p>
              </div>
              <AlertCircle className="w-8 h-8 text-orange-600" />
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div>
          <Label htmlFor="status">Status</Label>
          <Select value={filters.status} onValueChange={(value) => handleFilterChange('status', value)}>
            <SelectTrigger>
              <SelectValue placeholder="All statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="new">New</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="resolved">Resolved</SelectItem>
              <SelectItem value="closed">Closed</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label htmlFor="type">Type</Label>
          <Select value={filters.feedback_type} onValueChange={(value) => handleFilterChange('feedback_type', value)}>
            <SelectTrigger>
              <SelectValue placeholder="All types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              <SelectItem value="bug">Bug Report</SelectItem>
              <SelectItem value="feature_request">Feature Request</SelectItem>
              <SelectItem value="general">General Feedback</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label htmlFor="priority">Priority</Label>
          <Select value={filters.priority} onValueChange={(value) => handleFilterChange('priority', value)}>
            <SelectTrigger>
              <SelectValue placeholder="All priorities" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All priorities</SelectItem>
              <SelectItem value="urgent">Urgent</SelectItem>
              <SelectItem value="high">High</SelectItem>
              <SelectItem value="medium">Medium</SelectItem>
              <SelectItem value="low">Low</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label htmlFor="search">Search</Label>
          <div className="flex gap-2">
            <Input
              id="search"
              placeholder="Search..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
            />
            <Button onClick={() => loadFeedback()} size="sm" variant="outline">
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Feedback List */}
      {feedback.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          No feedback found matching your criteria.
        </div>
      ) : (
        <div className="space-y-4">
          {feedback.map((item) => (
            <div key={item.id} className="border rounded-lg p-4 hover:bg-gray-50 transition-colors">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    {getTypeIcon(item.feedback_type)}
                    <h3 className="font-semibold text-gray-900">
                      {item.title || `${item.feedback_type.charAt(0).toUpperCase() + item.feedback_type.slice(1)} Feedback`}
                    </h3>
                    <Badge className={getStatusColor(item.status)}>
                      {getStatusIcon(item.status)}
                      <span className="ml-1">{item.status}</span>
                    </Badge>
                    <Badge className={getPriorityColor(item.priority)}>
                      {item.priority}
                    </Badge>
                  </div>
                  
                  <p className="text-gray-600 mb-3 line-clamp-2">
                    {item.description}
                  </p>
                  
                  <div className="flex items-center gap-4 text-sm text-gray-500">
                    <div className="flex items-center gap-1">
                      <User className="w-4 h-4" />
                      <span>{item.user_email || 'Anonymous'}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      <span>{formatDate(item.created_at)}</span>
                    </div>
                  </div>
                </div>
                
                <div className="flex gap-2 ml-4">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleViewDetails(item)}
                  >
                    <Eye className="w-4 h-4 mr-1" />
                    View
                  </Button>
                  
                  {item.status === 'new' && (
                    <Button
                      size="sm"
                      onClick={() => handleUpdateStatus(item.id, 'in_progress')}
                      disabled={isUpdating}
                    >
                      Start
                    </Button>
                  )}
                  
                  {item.status === 'in_progress' && (
                    <Button
                      size="sm"
                      onClick={() => handleUpdateStatus(item.id, 'resolved')}
                      disabled={isUpdating}
                    >
                      Resolve
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Detail Modal */}
      <Dialog open={isDetailModalOpen} onOpenChange={setIsDetailModalOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Feedback Details</DialogTitle>
          </DialogHeader>
          
          {selectedFeedback && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium text-gray-500">Type</Label>
                  <div className="flex items-center gap-2 mt-1">
                    {getTypeIcon(selectedFeedback.feedback_type)}
                    <span className="capitalize">{selectedFeedback.feedback_type.replace('_', ' ')}</span>
                  </div>
                </div>
                
                <div>
                  <Label className="text-sm font-medium text-gray-500">Status</Label>
                  <div className="mt-1">
                    <Badge className={getStatusColor(selectedFeedback.status)}>
                      {getStatusIcon(selectedFeedback.status)}
                      <span className="ml-1">{selectedFeedback.status}</span>
                    </Badge>
                  </div>
                </div>
              </div>

              <div>
                <Label className="text-sm font-medium text-gray-500">Description</Label>
                <div className="mt-2 p-4 bg-gray-50 rounded-lg">
                  <p className="whitespace-pre-wrap">{selectedFeedback.description}</p>
                </div>
              </div>

              <div>
                <Label className="text-sm font-medium text-gray-500">Admin Notes</Label>
                <Textarea
                  placeholder="Add admin notes..."
                  defaultValue={selectedFeedback.admin_notes || ''}
                  rows={3}
                  onChange={(e) => {
                    setSelectedFeedback(prev => ({
                      ...prev,
                      admin_notes: e.target.value
                    }));
                  }}
                />
              </div>

              <div className="flex gap-3 pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={() => setIsDetailModalOpen(false)}
                >
                  Close
                </Button>
                
                <Button
                  onClick={() => {
                    handleUpdateStatus(selectedFeedback.id, 'in_progress', selectedFeedback.admin_notes);
                    setIsDetailModalOpen(false);
                  }}
                  disabled={isUpdating}
                >
                  Mark In Progress
                </Button>
                
                <Button
                  onClick={() => {
                    handleUpdateStatus(selectedFeedback.id, 'resolved', selectedFeedback.admin_notes);
                    setIsDetailModalOpen(false);
                  }}
                  disabled={isUpdating}
                >
                  Mark Resolved
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminFeedback;
